import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import APP_ROUTES from "../../../routers/routeConstants";
import { colors } from "../../../theme/palette";
import { isCurrentRoute } from "../../../routers/routeUtils";
import { CreatePaymentLinkButtonIcon, PaymentEgyptFlagIcon } from "../../../assets/icons/MUI/PaymentIcons";

import { PaymentHeaderProps } from "../payment.types";
import CantCreatePaymentLinkPopUp from "../PaymentOverview/NoPaymentAccountView/CantCreatePaymentLinkPopUp";

const PaymentHeader = ({
  openLearnMorePopUp,
  handleSetupPaymentAccount,
  disableCanCreatePaymentLink,
}: PaymentHeaderProps) => {
  const [isCantCreatePaymentLinkPopUpOpen, setIsCantCreatePaymentLinkPopUpOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = [
    { route: APP_ROUTES.PAYMENT, label: t("pages.payment.header.overview") },
    { route: APP_ROUTES.PAYMENT_LINKS, label: t("pages.payment.header.paymentLinks") },
  ];

  const closeCantCreatePaymentLinkPopUp = () => setIsCantCreatePaymentLinkPopUpOpen(false);
  const openCantCreatePaymentLinkPopUp = () => setIsCantCreatePaymentLinkPopUpOpen(true);

  return (
    <>
      <Box sx={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "6px" }}>
        <Typography
          sx={{
            color: "#1C1C1C",
            fontFamily: "Montserrat",
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "30px",
          }}
        >
          {t("pages.payment.header.paymentBilling")}
        </Typography>
      </Box>
      <Typography
        sx={{
          color: "#111",
          fontFamily: "Montserrat",
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "20px",
          marginBottom: "12px",
        }}
      >
        {t("pages.payment.header.helpToday")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            border: "1px solid #D0D5DD",
            height: "fit-content",
            borderRadius: "8px",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            overflow: "hidden",
            width: "fit-content",
          }}
        >
          {tabs.map((tab, idx) => (
            <Chip
              key={tab.route}
              label={tab.label}
              variant="outlined"
              disabled={!tab.route}
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                color: tab.route && isCurrentRoute(location.pathname, tab.route) ? colors.Green100 : "#344054",
                backgroundColor: tab.route && isCurrentRoute(location.pathname, tab.route) ? "#F0F5F4" : "",
                border: "none",
                borderRadius: "0",
                borderRight: idx === 0 ? "1px solid #D0D5DD" : "",
              }}
              onClick={() => tab.route && navigate(tab.route)}
            />
          ))}
        </Box>
        <Tooltip title={disableCanCreatePaymentLink ? t("pages.payment.header.pleaseAddPaymentAccount") : ""}>
          <span>
            <Button
              variant="primary-md"
              startIcon={<CreatePaymentLinkButtonIcon />}
              onClick={() => navigate(APP_ROUTES.PAYMENT_HOW_IT_WORKS)}
              disabled={disableCanCreatePaymentLink}
            >
              {t("pages.payment.header.createPaymentLink")}
            </Button>
          </span>
        </Tooltip>
        <CantCreatePaymentLinkPopUp
          openLearnMorePopUp={openLearnMorePopUp}
          open={isCantCreatePaymentLinkPopUpOpen}
          onClose={closeCantCreatePaymentLinkPopUp}
          handleSetupPaymentAccount={handleSetupPaymentAccount}
        />
      </Box>
    </>
  );
};

export default PaymentHeader;
